import { toRoman } from "../../lib/format";
import { domainLabel, domainSafetyNote, headlineAttribute, rankLabel } from "../../lib/manifest";
import { variationFor } from "../../lib/seed";
import { DateStamp } from "../DateStamp";
import { PhotoMount } from "../PhotoMount";
import { RarityBadge } from "../RarityBadge";
import { SafetyStrip } from "../SafetyStrip";
import type { ScrapbookTemplateProps } from "../ScrapbookPage";

/**
 * The field-guide plate, for fauna, fungi and geology.
 *
 * The photograph, plainly shown, then the handful of facts that matter: the
 * name, the binomial, and one headline attribute. Everything else Claude
 * wrote lives in the details disclosure further down the page — this is the
 * plate itself, not the annotations.
 */
export function FieldPlate({ entry, manifest, revealing = false, onImageError }: ScrapbookTemplateProps) {
  const variation = variationFor(entry.id);
  const domain = domainLabel(manifest, entry.domain);
  const rank = rankLabel(manifest, entry.domain, entry.rank);
  const headline = headlineAttribute(manifest, entry.domain, entry.attributes);

  return (
    <article className="sheet grain relative overflow-hidden">
      {/* Plate header: the running head of a field guide. */}
      <header className="flex items-baseline justify-between gap-3 border-b border-rule bg-paper-deep px-4 py-2.5 sm:px-6">
        <p className="field-label text-ink">Plate {toRoman(variation.plateNumber)}</p>
        <p className="field-label-tight text-ink-soft">
          {[domain, rank].filter(Boolean).join(" · ") || "Field plate"}
        </p>
      </header>

      <div className="px-4 pt-6 pb-6 sm:px-8 sm:pt-8 sm:pb-10">
        <figure className="mx-auto max-w-2xl">
          <PhotoMount
            seedId={entry.id}
            src={entry.imageUrl ?? entry.thumbUrl}
            alt={entry.displayTitle}
            aspect="aspect-4/3"
            revealing={revealing}
            eager
            {...(onImageError ? { onImageError } : {})}
          />
        </figure>

        <div className={revealing ? "animate-settle" : undefined}>
          <div className="mt-8 flex flex-wrap items-end justify-between gap-4">
            <div className="min-w-0">
              <h1 className="display-3 text-3xl leading-[1.1] text-ink sm:text-[2.5rem]">
                {entry.displayTitle}
              </h1>
              {entry.scientificName ? (
                <p className="mt-1.5 font-prose text-lg italic text-ink-soft">{entry.scientificName}</p>
              ) : null}
              {headline ? (
                <p className="mt-2 font-label text-sm text-ink-soft">
                  {headline.label}: <span className="text-ink">{headline.display}</span>
                </p>
              ) : null}
            </div>
            <div className="flex shrink-0 flex-col items-end gap-2">
              <RarityBadge rarity={entry.rarity} />
              <DateStamp date={entry.foundAt} seedId={entry.id} size="sm" />
            </div>
          </div>

          {entry.safetyNote ? (
            <SafetyStrip
              note={entry.safetyNote}
              domainNote={domainSafetyNote(manifest, entry.domain)}
              className="mt-6"
            />
          ) : null}
        </div>
      </div>
    </article>
  );
}
